import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FileText, FileSpreadsheet, FileType2, Mic, Film, Globe, Image as ImageIcon,
  Trash2, Loader2, CheckCircle2, AlertTriangle, Plus, Upload, RotateCw,
  ChevronDown, ChevronRight, PanelRightClose, PanelRightOpen,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import FileSummaryCard from "./FileSummaryCard";
import { FILE_ACCEPT } from "../../lib/fileTypes";

// ---------------------------------------------------------------------------
// Category mapping
// ---------------------------------------------------------------------------
const DOC_EXT = ["pdf", "docx", "doc", "xlsx", "xls", "pptx", "ppt", "txt", "md", "csv", "rtf", "json", "xml"];
const IMG_EXT = ["png", "jpg", "jpeg", "webp", "gif", "heic", "heif", "tif", "tiff", "bmp", "svg"];
const AUDIO_EXT = ["mp3", "wav", "m4a", "flac", "ogg", "aac", "opus"];
const VIDEO_EXT = ["mp4", "mov", "avi", "mkv", "webm", "mpeg", "mpg", "wmv", "flv"];

function extOf(name = "") {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

function categoryOf(file) {
  if (file.kind === "url" || file.source_type === "url") return "links";
  const ext = extOf(file.name || "");
  const k = file.kind;
  if (k === "audio" || AUDIO_EXT.includes(ext)) return "audio";
  if (k === "video" || VIDEO_EXT.includes(ext)) return "video";
  if (k === "image" || IMG_EXT.includes(ext)) return "images";
  if (DOC_EXT.includes(ext) || k === "doc") return "documents";
  return "documents";
}

const CATEGORIES = [
  { id: "documents", labelKey: "storage.cat.documents", Icon: FileText },
  { id: "images",    labelKey: "storage.cat.images",    Icon: ImageIcon },
  { id: "audio",     labelKey: "storage.cat.audio",     Icon: Mic },
  { id: "video",     labelKey: "storage.cat.video",     Icon: Film },
  { id: "links",     labelKey: "storage.cat.links",     Icon: Globe },
];

const iconForFile = (file) => {
  const c = categoryOf(file);
  const ext = extOf(file.name || "");
  if (c === "audio") return <Mic className="h-4 w-4" />;
  if (c === "video") return <Film className="h-4 w-4" />;
  if (c === "links") return <Globe className="h-4 w-4" />;
  if (c === "images") return <ImageIcon className="h-4 w-4" />;
  if (["xlsx", "xls", "csv"].includes(ext)) return <FileSpreadsheet className="h-4 w-4" />;
  if (["pdf"].includes(ext)) return <FileType2 className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
};

const statusBadge = (status) => {
  if (status === "ready") return <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />;
  if (status === "error") return <AlertTriangle className="h-3.5 w-3.5 text-destructive" />;
  return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />;
};

const statusLabel = (f) => {
  if (f.status === "ready") return "ready";
  if (f.status === "error") return "error";
  if (f.status === "uploading") return "uploading…";
  if (f.status === "queued") return "queued";
  if (f.status === "downloading_media") return "downloading audio…";
  if (f.status === "extracting_meta") return "reading structure…";
  if (f.status === "converting") return "extracting text…";
  if (f.status === "vlm_processing") return "running VLM…";
  if (f.status === "transcribing") return "transcribing…";
  if (f.status === "chunking") return "chunking…";
  if (f.status === "embedding") return "embedding for RAG…";
  return f.status;
};

const COLLAPSED_KEY = "nexa_storage_collapsed_v1";

// ---------------------------------------------------------------------------
export default function StoragePanel({ files, attached, onToggleAttach, onDelete, onUpload, onUrl, onRetry, onRefreshFiles, open = true, onToggleOpen }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [urlValue, setUrlValue] = useState("");
  const [drag, setDrag] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || "{}"); } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed)); } catch {}
  }, [collapsed]);

  const grouped = useMemo(() => {
    const out = { documents: [], images: [], audio: [], video: [], links: [] };
    for (const f of files) out[categoryOf(f)]?.push(f);
    return out;
  }, [files]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f);
  };

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  // Collapsed rail mode — narrow vertical strip with file count + expand button
  if (!open) {
    return (
      <div className="flex flex-col items-center py-3 gap-3 border-l border-border" data-testid="storage-panel-collapsed">
        <button
          onClick={onToggleOpen}
          className="p-1.5 rounded-sm hover:bg-secondary transition text-muted-foreground hover:text-foreground"
          data-testid="storage-expand-button"
          title={t("workspace:storage.expand", "Expandir almacenamiento")}
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground writing-vertical" style={{ writingMode: "vertical-rl" }}>
          {t("workspace:storage.rail", "STORAGE")} · {files.length}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">{t("workspace:storage.title")}</div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-muted-foreground">{files.length}</span>
          {onToggleOpen && (
            <button
              onClick={onToggleOpen}
              className="p-0.5 rounded-sm hover:bg-secondary text-muted-foreground hover:text-foreground transition"
              data-testid="storage-collapse-button"
              title={t("workspace:storage.collapse", "Colapsar panel")}
            >
              <PanelRightClose className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-2 border-b border-border">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed ${drag ? "border-primary bg-primary/5" : "border-border"} rounded-sm p-4 text-center cursor-pointer transition-colors`}
          data-testid="dropzone"
        >
          <Upload className="h-4 w-4 mx-auto mb-1.5 text-muted-foreground" />
          <div className="text-xs text-muted-foreground">
            {t("workspace:storage.dropzone", "Soltá o hacé clic para subir")}<br />
            <span className="font-mono">PDF · DocX · XLSX · PPT · TXT · imágenes · audio · video</span>
          </div>
          <input
            type="file"
            hidden
            ref={fileRef}
            accept={FILE_ACCEPT}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            data-testid="storage-file-input"
          />
        </div>
        <div className="flex gap-1.5">
          <Input
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder={t("workspace:storage.url_placeholder", "Pegá una URL…")}
            className="h-9 rounded-sm text-sm"
            data-testid="storage-url-input"
          />
          <Button
            size="sm"
            className="h-9 rounded-sm"
            disabled={!urlValue.trim()}
            onClick={() => {
              onUrl(urlValue.trim());
              setUrlValue("");
            }}
            data-testid="storage-url-submit"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {files.length === 0 && (
          <div className="p-4 text-xs text-muted-foreground">
            {t("workspace:storage.empty_long", "Todavía no hay nada acá. Los archivos extraídos vía Tensornex se vuelven contexto reutilizable.")}
          </div>
        )}

        {CATEGORIES.map(({ id, labelKey, Icon }) => {
          const items = grouped[id] || [];
          if (items.length === 0) return null;
          const isCollapsed = !!collapsed[id];
          return (
            <section key={id} className="border-b border-border last:border-b-0" data-testid={`storage-section-${id}`}>
              <button
                onClick={() => toggle(id)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-background transition-colors"
                data-testid={`storage-section-toggle-${id}`}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed
                    ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs uppercase tracking-[0.2em] font-mono">{t(`workspace:${labelKey}`)}</span>
                </div>
                <span className="text-xs font-mono text-muted-foreground">{items.length}</span>
              </button>

              {!isCollapsed && (
                <ul className="divide-y divide-border">
                  {items.map((f) => (
                    <li
                      key={f.id}
                      className={`px-3 py-2.5 hover:bg-background transition-colors ${
                        attached.includes(f.id) ? "bg-primary/5 border-l-2 border-primary" : ""
                      }`}
                      data-testid={`storage-file-${f.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5">{iconForFile(f)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="text-sm truncate font-medium" title={f.name}>{f.name}</div>
                            {statusBadge(f.status)}
                            <FileSummaryCard file={f} onRefreshed={onRefreshFiles} />
                          </div>
                          <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                            {f.kind} · {statusLabel(f)}
                            {f.sub_status && <span> · {f.sub_status}</span>}
                            {typeof f.progress_pct === "number" && f.status !== "ready" && f.status !== "error" && (
                              <span> · {f.progress_pct}%</span>
                            )}
                            {f.error && <span className="text-destructive"> · {f.error}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Button
                              size="sm"
                              variant={attached.includes(f.id) ? "default" : "outline"}
                              className="h-7 px-2 rounded-sm text-xs"
                              disabled={f.status !== "ready"}
                              onClick={() => onToggleAttach(f.id)}
                              data-testid={`attach-toggle-${f.id}`}
                            >
                              {attached.includes(f.id) ? t("workspace:storage.attached", "Adjunto") : t("workspace:storage.attach", "Adjuntar")}
                            </Button>
                            {(f.status === "error" || f.tensornex_status === "pending") && (
                              <button
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => onRetry?.(f.id)}
                                data-testid={`retry-file-${f.id}`}
                                aria-label={t("workspace:storage.retry", "Reintentar")}
                                title={t("workspace:storage.retry_extraction", "Reintentar extracción")}
                              >
                                <RotateCw className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => onDelete(f.id)}
                              data-testid={`delete-file-${f.id}`}
                              aria-label={t("workspace:storage.delete", "Borrar")}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
